/**
 * Agent WebSocket API 客户端
 * 使用 WebSocket 替代 HTTP，避免浏览器 CORS 限制
 */

class AgentWebSocket {
    private ws: WebSocket | null = null
    private pendingRequests = new Map<string, { resolve: (value: any) => void; reject: (reason?: any) => void; timeout: any }>()
    private reconnectTimer: any = null
    private reconnectAttempts = 0
    private maxReconnectAttempts = 1 // 最多重连2次
    private reconnectDelay = 1000 // 初始重连延迟（毫秒）
    private isConnecting = false
    private isConnected = false
    private hasFailedMaxReconnect = false // 标记是否已达到最大重连次数
    private messageQueue: Array<{ method: string; params: any; resolve: (value: any) => void; reject: (reason?: any) => void }> = []

    // 配置
    private url = 'ws://127.0.0.1:18888/ws'
    private pingInterval = 30000 // 30秒心跳
    private pingTimer: any = null
    private requestTimeout = 10000 // 10秒请求超时

    /**
     * 连接 WebSocket
     */
    connect(): Promise<void> {
        // 如果已经达到最大重连次数，直接拒绝连接
        if (this.hasFailedMaxReconnect) {
            return Promise.reject(new Error('WebSocket 连接失败，已达到最大重连次数'))
        }

        if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
            return Promise.resolve()
        }

        return new Promise((resolve, reject) => {
            this.isConnecting = true
            console.log('[AgentWS] 正在连接 WebSocket...', this.url)

            try {
                this.ws = new WebSocket(this.url)

                this.ws.onopen = () => {
                    console.log('[AgentWS] ✓ WebSocket 连接成功')
                    this.isConnecting = false
                    this.isConnected = true
                    this.reconnectAttempts = 0
                    this.reconnectDelay = 1000
                    this.hasFailedMaxReconnect = false // 连接成功，重置失败标志

                    // 启动心跳
                    this.startPing()

                    // 处理队列中的消息
                    this.processMessageQueue()

                    resolve()
                }

                this.ws.onmessage = (event) => {
                    try {
                        const response = JSON.parse(event.data as string)
                        this.handleMessage(response)
                    } catch (error) {
                        console.error('[AgentWS] ✗ 解析消息失败:', error, event.data)
                    }
                }

                this.ws.onerror = (error) => {
                    console.error('[AgentWS] ✗ WebSocket 错误:', error)
                    this.isConnecting = false
                    // 如果是第一次连接失败且还未开始重连，则 reject
                    // 否则让 onclose 处理重连逻辑
                    if (this.reconnectAttempts === 0 && !this.reconnectTimer) {
                        reject(error)
                    }
                }

                this.ws.onclose = (event) => {
                    console.log('[AgentWS] WebSocket 连接关闭:', event.code, event.reason)
                    this.isConnected = false
                    this.isConnecting = false
                    this.stopPing()

                    // 清理所有待处理的请求
                    this.clearPendingRequests('连接已断开')

                    // 自动重连（如果不是正常关闭）
                    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                        this.scheduleReconnect()
                    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                        // 达到最大重连次数，设置失败标志并清理消息队列
                        this.hasFailedMaxReconnect = true
                        this.clearMessageQueue('连接失败，已达到最大重连次数')
                    }
                }
            } catch (error) {
                this.isConnecting = false
                console.error('[AgentWS] ✗ 创建 WebSocket 失败:', error)
                reject(error)
            }
        })
    }

    /**
     * 处理收到的消息
     */
    private handleMessage(response: any) {
        const { id, success, data, error } = response

        if (this.pendingRequests.has(id)) {
            const { resolve, reject, timeout } = this.pendingRequests.get(id)!
            this.pendingRequests.delete(id)
            clearTimeout(timeout)

            if (success) {
                resolve(data)
            } else {
                reject(new Error(error || '未知错误'))
            }
        } else {
            // 可能是心跳响应或其他未匹配的消息
            console.log('[AgentWS] 收到未匹配的消息:', response)
        }
    }

    /**
     * 调用远程方法
     */
    async call(method: string, params: any = {}): Promise<any> {
        // 如果已经达到最大重连次数，直接拒绝，不再尝试连接
        if (this.hasFailedMaxReconnect) {
            throw new Error('WebSocket 连接失败，已达到最大重连次数')
        }

        // 确保连接
        if (!this.isConnected) {
            try {
                await this.connect()
            } catch (error) {
                // 如果已经达到最大重连次数，直接拒绝
                if (this.hasFailedMaxReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
                    throw new Error('WebSocket 连接失败，已达到最大重连次数')
                }
                // 连接失败，将消息加入队列
                return new Promise((resolve, reject) => {
                    this.messageQueue.push({ method, params, resolve, reject })
                })
            }
        }

        // 生成请求ID
        const id = this.generateRequestId()

        // 构建消息
        const message = {
            id,
            method,
            params
        }

        return new Promise((resolve, reject) => {
            // 设置超时
            const timeout = setTimeout(() => {
                if (this.pendingRequests.has(id)) {
                    this.pendingRequests.delete(id)
                    reject(new Error(`请求超时: ${method}`))
                }
            }, this.requestTimeout)

            // 存储请求
            this.pendingRequests.set(id, { resolve, reject, timeout })

            // 发送消息
            try {
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify(message))
                    // console.log('[AgentWS] 发送请求:', method, params)
                } else {
                    // 连接未就绪
                    clearTimeout(timeout)
                    this.pendingRequests.delete(id)

                    // 如果已经达到最大重连次数，直接拒绝
                    if (this.hasFailedMaxReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
                        reject(new Error('WebSocket 连接失败，已达到最大重连次数'))
                        return
                    }

                    // 否则加入队列并尝试重连
                    this.messageQueue.push({ method, params, resolve, reject })
                    this.connect().catch(() => {
                        // 如果重连失败且已达到最大重连次数，清理队列
                        if (this.hasFailedMaxReconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
                            this.clearMessageQueue('WebSocket 连接失败，已达到最大重连次数')
                        }
                    })
                }
            } catch (error) {
                clearTimeout(timeout)
                this.pendingRequests.delete(id)
                // console.error('[AgentWS] ✗ 发送消息失败:', error)
                reject(error)
            }
        })
    }

    /**
     * 处理消息队列
     */
    private processMessageQueue() {
        if (this.messageQueue.length === 0) {
            return
        }

        // console.log(`[AgentWS] 处理 ${this.messageQueue.length} 条队列消息`)
        const queue = [...this.messageQueue]
        this.messageQueue = []

        queue.forEach(({ method, params, resolve, reject }) => {
            this.call(method, params).then(resolve).catch(reject)
        })
    }

    /**
     * 清理待处理的请求
     */
    private clearPendingRequests(reason: string) {
        const count = this.pendingRequests.size
        if (count > 0) {
            // console.log(`[AgentWS] 清理 ${count} 个待处理请求:`, reason)
            this.pendingRequests.forEach(({ reject, timeout }) => {
                clearTimeout(timeout)
                reject(new Error(reason))
            })
            this.pendingRequests.clear()
        }
    }

    /**
     * 清理消息队列
     */
    private clearMessageQueue(reason: string) {
        const count = this.messageQueue.length
        if (count > 0) {
            // console.log(`[AgentWS] 清理 ${count} 条队列消息:`, reason)
            this.messageQueue.forEach(({ reject }) => {
                reject(new Error(reason))
            })
            this.messageQueue = []
        }
    }

    /**
     * 安排重连
     */
    private scheduleReconnect() {
        if (this.reconnectTimer) {
            return
        }

        this.reconnectAttempts++
        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000)

        // console.log(`[AgentWS] ${delay}ms 后尝试重连 (第 ${this.reconnectAttempts} 次)...`)

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            this.connect().catch(() => {
                // 重连失败，继续尝试
                if (this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.scheduleReconnect()
                } else {
                    // console.error('[AgentWS] ✗ 达到最大重连次数，停止重连')
                    // 达到最大重连次数，设置失败标志并清理消息队列
                    this.hasFailedMaxReconnect = true
                    this.clearMessageQueue('连接失败，已达到最大重连次数')
                }
            })
        }, delay)
    }

    /**
     * 启动心跳
     */
    private startPing() {
        this.stopPing()
        this.pingTimer = setInterval(() => {
            if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.call('ping', {}).catch(() => {
                    // 心跳失败，连接可能已断开
                    // console.warn('[AgentWS] 心跳失败')
                })
            }
        }, this.pingInterval)
    }

    /**
     * 停止心跳
     */
    private stopPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer)
            this.pingTimer = null
        }
    }

    /**
     * 断开连接
     */
    disconnect() {
        this.stopPing()

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        this.reconnectAttempts = this.maxReconnectAttempts // 阻止自动重连
        this.hasFailedMaxReconnect = false // 手动断开时重置失败标志，允许下次连接

        if (this.ws) {
            this.ws.close(1000, '手动断开')
            this.ws = null
        }

        this.clearPendingRequests('手动断开连接')
        this.isConnected = false
    }

    /**
     * 生成请求ID
     */
    private generateRequestId(): string {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }

    // ========== API 方法 ==========

    /**
     * 获取设备信息
     */
    async getDeviceInfo() {
        return await this.call('getDeviceInfo', {})
    }

    /**
     * 签名 challenge
     */
    async signChallenge(challenge: string) {
        return await this.call('signChallenge', { challenge })
    }

    /**
     * 注册设备
     */
    async registerDevice(deviceId: string, deviceName: string, os: string, friendlyName?: string, authToken?: string) {
        return await this.call('registerDevice', {
            device_id: deviceId,
            device_name: deviceName,
            os: os,
            friendly_name: friendlyName,
            auth_token: authToken
        })
    }

    /**
     * 健康检查
     */
    async healthCheck() {
        return await this.call('healthCheck', {})
    }
}

// 创建单例
const agentWebSocket = new AgentWebSocket()

// 兼容原有 API 接口
const agentApi = {
    // 初始化连接（可选）
    async connect() {
        return await agentWebSocket.connect()
    },

    // 断开连接（可选）
    disconnect() {
        agentWebSocket.disconnect()
    },

    // GET /api/device/info
    async get(url: string) {
        if (url === '/info' || url === '/api/device/info') {
            const data = await agentWebSocket.getDeviceInfo()
            return { data }
        }
        throw new Error('Unknown endpoint')
    },

    // POST /api/device/sign
    async post(url: string, data: any) {
        if (url === '/sign' || url === '/api/device/sign') {
            const result = await agentWebSocket.signChallenge(data.challenge)
            return { data: result }
        }
        if (url === '/register' || url === '/api/device/register') {
            const result = await agentWebSocket.registerDevice(
                data.device_id,
                data.device_name,
                data.os,
                data.friendly_name || data.device_alias,
                data.auth_token
            )
            return { data: result }
        }
        throw new Error('Unknown endpoint')
    }
}

export default agentApi

